import { useEffect, useMemo, useState } from "preact/hooks";
import type { ConvData, Language, NegativeReplyResult, TabDef } from "./types";
import {
  ApiError,
  createConversationShareLink,
  fetchConv,
  hasStoredAuthToken,
  isValidConvId,
  parseUrl,
  recordMatchFeedback,
  sendConversationNegativeReply,
} from "./api";
import { Topbar } from "./components/Topbar";
import { Tabs } from "./components/Tabs";
import { ResumoTab } from "./components/tabs/ResumoTab";
import { ConversaTab } from "./components/tabs/ConversaTab";
import { MatchesTab } from "./components/tabs/MatchesTab";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ClientPage } from "./pages/ClientPage";
import { ClientIndexPage } from "./pages/ClientIndexPage";
import { AnalysisPage } from "./pages/AnalysisPage";
import { ErrorPage, NotFoundPage, UnauthorizedPage } from "./pages/StatusPage";

type TabKey = "resumo" | "conversa" | "matches";

function conversationShareUrl(conversationId: string, token: string | null): string {
  const url = new URL(`/conv/${conversationId}`, window.location.origin);
  if (token) url.searchParams.set("token", token);
  return url.toString();
}

export function App() {
  const path = window.location.pathname;
  const url = parseUrl();
  const mock = url.mock;
  const isLogin = path === "/login";
  const isConv = path.startsWith("/conv/");
  const isDashboard = path === "/" || path === "/conversas";
  const isClient = path.startsWith("/clientes/");
  const isClientIndex = path === "/clientes";
  const isAnalysis = path === "/analise";
  const isUnauthorized = path === "/unauthorized";
  const isKnownPath = isDashboard || isConv || isClient || isClientIndex || isAnalysis;

  if (isLogin) return <LoginPage />;
  if (isUnauthorized) return <UnauthorizedPage />;
  if (path === "/") {
    const search = window.location.search || "";
    const next = encodeURIComponent(`/conversas${search}`);
    window.location.replace(hasStoredAuthToken() ? `/conversas${search}` : `/login?next=${next}`);
    return null;
  }
  if (path === "/alertas") {
    window.location.replace(`/conversas${window.location.search || ""}`);
    return null;
  }
  if (!isKnownPath) return <NotFoundPage detail="A rota solicitada não existe no wir-app." />;
  if (!isConv && !hasStoredAuthToken()) {
    const next = `${path}${window.location.search || ""}`;
    return <UnauthorizedPage key={next} next={next} />;
  }
  if (isConv) return <ConversationPage />;
  if (isClientIndex) return <ClientIndexPage mock={mock} />;
  if (isClient) return <ClientPage mock={mock} />;
  if (isAnalysis) return <AnalysisPage mock={mock} />;
  if (isDashboard) return <DashboardPage mock={mock} />;
  return <NotFoundPage detail="A rota solicitada não existe no wir-app." />;
}

function ConversationPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("resumo");
  const [data, setData] = useState<ConvData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareToken, setShareToken] = useState<string | null>(() => parseUrl().token);
  const [matchFeedbackError, setMatchFeedbackError] = useState<string | null>(null);
  const [matchFeedbackPendingId, setMatchFeedbackPendingId] = useState<string | null>(null);
  const [negativeReplyLanguage, setNegativeReplyLanguage] = useState<Language>("pt");
  const [negativeReplyPending, setNegativeReplyPending] = useState(false);
  const [negativeReplyError, setNegativeReplyError] = useState<string | null>(null);
  const isAdminAccess = hasStoredAuthToken();

  useEffect(() => {
    const url = parseUrl();
    if (!url.mock && !isValidConvId(url.id)) {
      setError("URL inválida — conversation_id ausente ou malformado.");
      setErrorStatus(404);
      setLoading(false);
      return;
    }
    fetchConv(url)
      .then((d) => {
        if (!d || d.ok === false) {
          setError(d?.error || "Conversa não encontrada.");
          setErrorStatus(d?.error === "conv_not_found" ? 404 : d?.error ? 401 : null);
        } else {
          setData(d);
          if (d.conv?.language === "pt" || d.conv?.language === "en" || d.conv?.language === "fr" || d.conv?.language === "es") {
            setNegativeReplyLanguage(d.conv.language);
          }
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(err);
        if (err instanceof ApiError) {
          setErrorStatus(err.status);
          setError(err.status === 404 ? "Conversa não encontrada." : msg);
          return;
        }
        setErrorStatus(null);
        setError("Erro ao carregar conversa. " + msg);
      })
      .finally(() => setLoading(false));
  }, []);

  const tabs = useMemo<TabDef<TabKey>[]>(() => {
    const arr: TabDef<TabKey>[] = [
      { key: "resumo", label: "Resumo" },
      {
        key: "conversa",
        label: "Conversa",
        count: Array.isArray(data?.messages)
          ? data.messages.filter((m) => !(m.is_system || m.role === "system"))
            .length
          : null,
      },
    ];
    if (Array.isArray(data?.matches) && data.matches.length > 0) {
      arr.push({ key: "matches", label: "Matches", count: data.matches.length });
    }
    return arr;
  }, [data]);

  if (loading) {
    return (
      <>
        <Topbar name="Carregando…" showBack={isAdminAccess} showShare={false} />
        <main class="app-main">
          <div class="state-msg">Carregando conversa…</div>
        </main>
      </>
    );
  }

  if (error || !data) {
    if (errorStatus === 401 || errorStatus === 403) return <UnauthorizedPage />;
    if (errorStatus === 404) {
      return <NotFoundPage title="Conversa não encontrada" detail={error} />;
    }
    return <ErrorPage detail={error} />;
  }

  const { conv, session, lead_data } = data;
  const leadEmail = lead_data?.email || null;
  const displayEmail = session?.user_email || leadEmail;
  const currentUrl = parseUrl();
  const token = shareToken || currentUrl.token;
  const shareUrl = conv?.id && token ? conversationShareUrl(conv.id, token) : null;

  async function createShareUrl(): Promise<string | null> {
    if (!conv?.id) return null;
    if (token) return conversationShareUrl(conv.id, token);
    if (currentUrl.mock) return conversationShareUrl(conv.id, null);

    const result = await createConversationShareLink(conv.id);
    if (!result.ok || !result.access_token) {
      throw new Error(result.error || "share_link_failed");
    }

    setShareToken(result.access_token);
    const url = conversationShareUrl(conv.id, result.access_token);
    window.history.replaceState(null, "", new URL(url).pathname + new URL(url).search);
    return url;
  }

  async function handleMatchFeedback(matchId: string | null | undefined, feedback: "good" | "bad") {
    if (!conv?.id || matchFeedbackPendingId) return;
    const stableMatchId = matchId || null;
    if (!stableMatchId) {
      setMatchFeedbackError("Match sem identificador de feedback.");
      return;
    }

    setMatchFeedbackError(null);
    setMatchFeedbackPendingId(stableMatchId);
    try {
      if (currentUrl.mock) {
        setData((current) => updateMatchFeedbackCounts(current, stableMatchId, feedback, 1, 0));
        return;
      }

      const result = await recordMatchFeedback(conv.id, stableMatchId, feedback, token);
      if (!result.ok) throw new Error(result.error || "match_feedback_failed");
      setData((current) =>
        updateMatchFeedbackCounts(
          current,
          stableMatchId,
          feedback,
          result.feedback_good_count,
          result.feedback_bad_count,
        )
      );
    } catch (err) {
      setMatchFeedbackError(err instanceof Error ? err.message : String(err));
    } finally {
      setMatchFeedbackPendingId(null);
    }
  }

  async function handleNegativeReply() {
    if (!conv?.id || negativeReplyPending) return;
    setNegativeReplyError(null);
    setNegativeReplyPending(true);
    try {
      let result: NegativeReplyResult;
      if (currentUrl.mock) {
        const option = data?.negative_reply_options?.[negativeReplyLanguage];
        result = {
          ok: true,
          conversation_id: conv.id,
          negative_reply: {
            status: "sent",
            language: negativeReplyLanguage,
            transport: "text",
            body: option?.body,
            requested_by: "mock",
            sent_at: new Date().toISOString(),
          },
        };
      } else {
        result = await sendConversationNegativeReply(conv.id, negativeReplyLanguage, token);
      }
      if (result.negative_reply) {
        setData((current) => current ? { ...current, negative_reply: result.negative_reply } : current);
      }
      if (!result.ok) throw new Error(result.error || "negative_reply_failed");
    } catch (err) {
      if (err instanceof ApiError && err.body && typeof err.body === "object") {
        const result = err.body as NegativeReplyResult;
        if (result.negative_reply) {
          setData((current) => current ? { ...current, negative_reply: result.negative_reply } : current);
        }
        setNegativeReplyError(result.error || err.message);
      } else {
        setNegativeReplyError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setNegativeReplyPending(false);
    }
  }

  const negativeReplyProps = {
    status: data.negative_reply,
    options: data.negative_reply_options,
    language: negativeReplyLanguage,
    languageLocked: data.conv?.language_established === true,
    pending: negativeReplyPending,
    error: negativeReplyError,
    onLanguageChange: setNegativeReplyLanguage,
    onSend: handleNegativeReply,
  };

  return (
    <>
      <div class="sticky-header">
        <Topbar
          name={session?.user_name}
          email={displayEmail}
          intent={conv?.intent}
          state={conv?.terminal_state}
          entityId={session?.ego_entity_id}
          opportunityId={conv?.ego_opportunity_id}
          opportunityUrl={conv?.ego_opportunity_url}
          leadId={conv?.ego_lead_id}
          leadUrl={conv?.ego_lead_url}
          crmFinalKind={conv?.crm_final_emission_kind}
          shareUrl={shareUrl}
          onCreateShareUrl={createShareUrl}
          showBack={isAdminAccess}
        />
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      </div>
      <main class="app-main">
        {activeTab === "resumo" && <ResumoTab data={data} negativeReply={negativeReplyProps} />}
        {activeTab === "conversa" && <ConversaTab messages={data.messages} negativeReply={negativeReplyProps} />}
        {activeTab === "matches" && (
          <MatchesTab
            matches={data.matches}
            onFeedback={(match, feedback) => handleMatchFeedback(match.match_id || match.id, feedback)}
            feedbackPendingId={matchFeedbackPendingId}
            feedbackError={matchFeedbackError}
          />
        )}
      </main>
    </>
  );
}

function updateMatchFeedbackCounts(
  current: ConvData | null,
  matchId: string,
  feedback: "good" | "bad",
  goodCount?: number,
  badCount?: number,
): ConvData | null {
  if (!current?.matches) return current;
  return {
    ...current,
    matches: current.matches.map((match) => {
      if ((match.match_id || match.id) !== matchId) return match;
      return {
        ...match,
        feedback_good_count: goodCount ?? (feedback === "good" ? 1 : 0),
        feedback_bad_count: badCount ?? (feedback === "bad" ? 1 : 0),
      };
    }),
  };
}
